import { Line as Outline } from '@react-three/drei';
import { useThree } from '@react-three/fiber';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BufferGeometry, BufferAttribute, Plane, Vector2, Mesh } from 'three';
import { useShallow } from 'zustand/shallow';

import { LayerType } from 'app/modules/wallforming/components/scenes/camera-controls/types/layerType';
import { convertToFloatArray } from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { computeWorkingPlaneData } from 'app/modules/wallforming/components/scenes/objects/articles/utils/dragUtils';
import { PoiNavigationSource } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiNavigationSource';
import { isPlanarFacePoi } from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';

interface FaceHighlightProps {
    selectedPoiIndex: number;
}

export const FaceHighlight = ({ selectedPoiIndex }: FaceHighlightProps) => {
    const size = useThree((state) => state.size);

    const {
        poi,
        mainWebGLRendererRef,
        mainCameraControlsRef,
        faceSelectionRef,
        setWorkingPlane,
        setFaceSelectionRef,
        setFocusOnWall
    } = usePlacementStore(
        useShallow((state) => ({
            poi: state.pois[selectedPoiIndex]?.poi ?? null,
            mainWebGLRendererRef: state.mainWebGLRendererRef,
            faceSelectionRef: state.faceSelectionRef,
            mainCameraControlsRef: state.mainCameraControlsRef,
            setWorkingPlane: state.setWorkingPlane,
            setFaceSelectionRef: state.setFaceSelectionRef,
            setFocusOnWall: state.setFocusOnWall
        }))
    );

    const prevIndexRef = useRef<number | undefined>(undefined);

    const planarPoi = useMemo(() => {
        if (!poi || !isPlanarFacePoi(poi) || !poi.formFace || !poi.formFace.outerWire) {
            return null;
        }

        return poi;
    }, [poi]);

    const edges = useMemo(() => planarPoi?.formFace.outerWire.edges ?? [], [planarPoi]);

    const vertexArray = useMemo(
        () => (planarPoi?.vertices ? convertToFloatArray(planarPoi.vertices) : undefined),
        [planarPoi]
    );

    const indexArray = useMemo(
        () => (planarPoi?.indices ? Uint32Array.from(planarPoi.indices) : undefined),
        [planarPoi]
    );

    const faceGeometry = useMemo(() => {
        if (!vertexArray) {
            return undefined;
        }

        const geometry = new BufferGeometry();
        geometry.setAttribute('position', new BufferAttribute(vertexArray, 3));

        if (indexArray) {
            geometry.setIndex(new BufferAttribute(indexArray, 1));
        }

        return geometry;
    }, [vertexArray, indexArray]);

    const edgePoints = useMemo(() => {
        if (!edges || edges.length < 2) {
            return [];
        }

        const points: number[] = [];

        for (let i = 0; i < edges.length - 1; i++) {
            const a = edges[i].startPoint;
            const b = edges[i + 1].startPoint;
            points.push(a.x, a.y, a.z, b.x, b.y, b.z);
        }

        const last = edges[edges.length - 1].startPoint;
        const first = edges[0].startPoint;
        points.push(last.x, last.y, last.z, first.x, first.y, first.z);

        return points;
    }, [edges]);

    const handleFaceMeshRef = useCallback(
        (mesh: Mesh | null) => {
            if (mesh) {
                setFaceSelectionRef(mesh);
            }
        },
        [setFaceSelectionRef]
    );

    useEffect(() => {
        if (selectedPoiIndex === undefined || selectedPoiIndex === prevIndexRef.current) {
            return;
        }

        if (mainCameraControlsRef.current && mainWebGLRendererRef.current && faceSelectionRef.current) {
            const state = usePlacementStore.getState();
            const source = state.poiSelection.current.source ?? PoiNavigationSource.PointerSelection;
            const isCameraAlignmentDeactivated = state.disableCameraAlignBySource[source];

            // Only align camera if toggle is enabled and 'Control' key is not pressed
            if (!isCameraAlignmentDeactivated) {
                setFocusOnWall(
                    mainCameraControlsRef,
                    faceSelectionRef.current,
                    mainWebGLRendererRef.current.domElement
                );
            }

            state.setDisableCameraAlign(PoiNavigationSource.PointerSelection, false);
            prevIndexRef.current = selectedPoiIndex;
        }

        return () => {
            faceGeometry?.dispose();
        };
    }, [selectedPoiIndex, mainCameraControlsRef, mainWebGLRendererRef, setFocusOnWall, faceGeometry, faceSelectionRef]);

    useEffect(() => {
        if (!faceGeometry) {
            return;
        }

        faceGeometry.computeVertexNormals();
        const { position, normal } = computeWorkingPlaneData(faceGeometry);
        const plane = new Plane().setFromNormalAndCoplanarPoint(normal, position);
        setWorkingPlane(plane);
    }, [setWorkingPlane, faceGeometry]);

    if (!planarPoi || !faceGeometry || edgePoints.length === 0) {
        return null;
    }

    return (
        <>
            <Outline
                color={'#649fda'}
                dashed={false}
                layers={[LayerType.DEFAULT]}
                lineWidth={3}
                points={edgePoints}
                resolution={new Vector2(size.width, size.height)}
            />

            <mesh ref={handleFaceMeshRef} layers={[LayerType.DEFAULT]}>
                <primitive attach="geometry" object={faceGeometry} />
                <meshBasicMaterial visible={false} />
            </mesh>
        </>
    );
};
